import React from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  CircularProgress,
  Divider,
  Stack,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAuthStore } from '../../store/authStore';
import GoogleSignInButton from '../../components/auth/GoogleSignInButton';
import Logo, { TAGLINE } from '../../components/brand/Logo';
import { LoginCredentials } from '../../types';

const schema = yup.object({
  email: yup
    .string()
    .email('Please enter a valid email address')
    .required('Email is required'),
  password: yup.string().required('Password is required'),
});

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoading, error, clearError } = useAuthStore();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginCredentials>({
    resolver: yupResolver(schema),
  });

  // Where the user was headed, query string included (a job board search, say).
  const withPassword = new URLSearchParams(location.search).get('password') === '1';
  const origin = (location.state as { from?: { pathname?: string; search?: string } } | null)?.from;
  // A note from the page that sent the person here, such as "your account has been deleted".
  const notice = (location.state as { message?: string } | null)?.message;
  const from = origin?.pathname ? `${origin.pathname}${origin.search ?? ''}` : '/dashboard';

  const onSubmit = async (data: LoginCredentials) => {
    try {
      clearError();
      await login(data);
      navigate(from, { replace: true });
    } catch {
      // Error is surfaced through the store.
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography component="h1" sx={{ m: 0, mb: 2.5, lineHeight: 1 }}>
          <Logo size={52} />
        </Typography>
        <Typography variant="h5" component="p" gutterBottom sx={{ fontWeight: 600 }}>
          {TAGLINE}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Sign in to continue your job search
        </Typography>
      </Box>

      <Paper elevation={4} sx={{ p: 4 }}>
        {notice && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {notice}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
            {error}
          </Alert>
        )}

        <GoogleSignInButton
          onSignedIn={() => navigate(from, { replace: true })}
          disabled={isLoading}
        />
        <Typography variant="caption" color="text.secondary" component="p" sx={{ textAlign: 'center', mt: 1.5 }}>
          By continuing you agree to the{' '}
          <Link component={RouterLink} to="/terms">
            Terms of Service
          </Link>{' '}
          and{' '}
          <Link component={RouterLink} to="/privacy">
            Privacy Policy
          </Link>
          .
        </Typography>

        {/* Accounts with a password are not offered any more; local test accounts still sign in with ?password=1. */}
        {withPassword && (
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Stack spacing={3}>
            <TextField
              label="Email address"
              type="email"
              autoComplete="email"
              fullWidth
              {...register('email')}
              error={!!errors.email}
              helperText={errors.email?.message}
              disabled={isLoading}
            />

            <TextField
              label="Password"
              type="password"
              autoComplete="current-password"
              fullWidth
              {...register('password')}
              error={!!errors.password}
              helperText={errors.password?.message}
              disabled={isLoading}
            />

            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={isLoading}
              sx={{ py: 1.5 }}
            >
              {isLoading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Signing in...
                </>
              ) : (
                'Sign in'
              )}
            </Button>
          </Stack>
        </form>
        )}

        <Divider sx={{ my: 3 }} />

        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            New to JobFlo? The same button creates your account.
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default LoginPage;
